import { useState, type ReactNode } from "react";
import { Link, useInRouterContext } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { Icon, IconButton, Popover } from "@wato/ui";

export interface FeatureIntroProps {
  /** Stable id of the feature (e.g. "sequences"); the collapsed state is remembered per user + key. */
  readonly featureKey: string;
  readonly title: string;
  /** One sentence: what the feature is. */
  readonly what: string;
  /** One sentence: when to use it. */
  readonly when: string;
  readonly steps?: readonly string[];
  /** Slot for an illustration (supplied later by the illustration set). */
  readonly illustration?: ReactNode;
  readonly helpHref?: string;
  /** Usually a `<ChatPreview />` showing the expected conversation. */
  readonly children?: ReactNode;
  /** Collapsed state renders only a small round «?» icon button (label/tooltip = the show text). */
  readonly compact?: boolean;
}

/**
 * "Explain this feature" help: a `?` icon button (44 px target) that opens what it is, when to use it,
 * optional steps and an example in a popover (bottom sheet on phones) — it no longer pushes the
 * page content down (OWN-173 G3). `data-feature-intro`/`data-state` stay for tests and tours.
 */
export function FeatureIntro({
  featureKey,
  title,
  what,
  when,
  steps,
  illustration,
  helpHref,
  children,
}: FeatureIntroProps) {
  const { t } = useI18n();
  const inRouter = useInRouterContext();
  const [open, setOpen] = useState(false);
  const linkClass = "w-fit text-sm font-medium text-on-action-ghost underline";

  return (
    <section
      aria-label={title}
      data-feature-intro={featureKey}
      data-state={open ? "expanded" : "collapsed"}
      className="inline-block"
    >
      <Popover
        label={title}
        open={open}
        onOpenChange={setOpen}
        panelClassName="w-80"
        trigger={(p) => (
          <IconButton
            {...p}
            label={t("featureIntro.show")}
            icon={<Icon name="help" />}
            variant="ghost"
          />
        )}
      >
        <div className="flex flex-col gap-3">
          <h3 className="text-base font-semibold text-fg">{title}</h3>
          {illustration ? (
            <div aria-hidden="true" className="max-w-32">
              {illustration}
            </div>
          ) : null}
          <dl className="m-0 flex flex-col gap-2 text-sm text-fg">
            <div>
              <dt className="font-semibold">{t("featureIntro.whatLabel")}</dt>
              <dd className="m-0 text-fg-muted">{what}</dd>
            </div>
            <div>
              <dt className="font-semibold">{t("featureIntro.whenLabel")}</dt>
              <dd className="m-0 text-fg-muted">{when}</dd>
            </div>
          </dl>
          {steps && steps.length > 0 ? (
            <div className="flex flex-col gap-1">
              <h4 className="text-sm font-semibold text-fg">{t("featureIntro.stepsLabel")}</h4>
              <ol className="m-0 list-decimal ps-5 text-sm text-fg-muted">
                {steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          ) : null}
          {helpHref ? (
            inRouter && helpHref.startsWith("/") ? (
              <Link to={helpHref} className={linkClass}>
                {t("featureIntro.learnMore")}
              </Link>
            ) : (
              <a
                href={helpHref}
                className={linkClass}
                {...(/^https?:\/\//.test(helpHref)
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                {t("featureIntro.learnMore")}
              </a>
            )
          ) : null}
          {children ? <div>{children}</div> : null}
        </div>
      </Popover>
    </section>
  );
}
